<template>
	<div v-if="acceptOnlineOrder" class="order-type-switcher">
		<button
			v-if="acceptForHere && !isDesktopDevice"
			class="button"
			:class="{
				selected: isInVenue,
				disabled: isTabOpened,
				'read-only': isLoading
			}"
			type="button"
			:disabled="isTabOpened || isLoading"
			@click="changeOrderType($options.orderTypes.isInVenue)"
		>
			Dine In
		</button>

		<pick-up-time-picker
			v-if="acceptPickUp"
			:class="{
				selected: isPickUp,
				disabled: isTabOpened,
				'read-only': isLoading,
				'pick-up-only': !acceptForHere || isDesktopDevice
			}"
			:pick-up-time="pickUpTime"
			:pick-up-time-from="currentPickUpTimeFrom"
			:pick-up-time-to="currentPickUpTimeTo"
			:is-pick-up-time-valid="isPickUpTimeValid"
			:show-pick-up-time-picker="showPickUpTimePicker"
			:has-error="!isPickUpTimeValid"
			check-on-open
			need-scroll-after-opening
			opener-type="button"
			@change-time="changePickUpTime"
			@picker-opened="changeOrderType($options.orderTypes.isPickUp, $event)"
		/>
	</div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'

import { ORDER_TYPES } from '@/constants'

import routerMixin from '@/mixins/routerMixin'
import PickUpTimePicker from '@/viewsOM/Cart/PickUpTimePicker.vue'
import pickUpValidation from '@/mixins/pickUpValidation'

export default {
	name: 'OrderTypeSwitcher',
	components: { PickUpTimePicker },

	mixins: [pickUpValidation, routerMixin],

	orderTypes: ORDER_TYPES,

	data() {
		return {
			currentPickUpTimeFrom: '',
			currentPickUpTimeTo: '',
			isPickUpTimeValid: true,
			showPickUpTimePicker: 0,
			isLoading: false
		}
	},

	created() {
		this.initPickupInfo()
	},

	computed: {
		...mapState({
			cart: state => state.cart,
			venueInfo: state => state.venue.venueInfo
		}),

		...mapState('venue', [
			'isToGoOrderingAvailable',
			'isForHereOrderingAvailable',
			'pickUpTime'
		]),
		...mapState('ui', ['isDesktopDevice']),
		...mapState('cart', ['isTabFailed', 'pickupTimeFrom', 'pickupTimeTo']),

		...mapGetters('cart', ['isTabOpened']),

		acceptToGo() {
			return this.venueInfo.acceptToGo
		},

		acceptForHere() {
			return this.venueInfo.acceptForHere
		},

		acceptPickUp() {
			return this.venueInfo.acceptPickUp
		},

		isInVenue() {
			return this.cart.isInVenue
		},

		isPickUp() {
			return this.cart.isPickUp
		},

		acceptOnlineOrder() {
			return this.venueInfo.acceptOnlineOrder
		}
	},

	watch: {
		cart: {
			deep: true,
			handler(val) {
				if (val) {
					this.$nextTick(() => {
						this.initPickupInfo()
					})
				}
			}
		}
	},

	methods: {
		...mapActions('venue', ['setAllOrderingAvailable']),
		...mapActions('cart', ['updateCart']),

		async changeOrderType(orderType, isPickupUnavailable = false) {
			const isCheckedTypeSameAsCurrent = orderType === this.cart.orderType

			if (
				(isPickupUnavailable && orderType === ORDER_TYPES.isPickUp) ||
				isCheckedTypeSameAsCurrent
			) {
				return
			}

			this.isLoading = true
			// Without await
			this.setAllOrderingAvailable()
			// TODO remove isPickUp and isInVenue from the master
			const payload = {
				orderType,
				isPickUp: orderType === ORDER_TYPES.isPickUp,
				isInVenue: orderType === ORDER_TYPES.isInVenue,
				needImmediateUpdate: true
			}

			if (orderType === ORDER_TYPES.isInVenue) {
				payload.includeUtensils = false
			}

			if (this.cart.cartItems?.length) payload.isNeedShowExpiredPopup = true

			await this.callUpdateCart(payload)

			this.isLoading = false
		},

		async callUpdateCart(payload) {
			try {
				await this.updateCart(payload)
			} catch (e) {}
		},

		initPickupInfo() {
			const isPickUpTimeValid = this.pickUpValidation(
				this.pickupTimeFrom,
				this.pickupTimeTo
			)

			this.currentPickUpTimeFrom = isPickUpTimeValid
				? this.pickupTimeFrom
				: null
			this.currentPickUpTimeTo = isPickUpTimeValid ? this.pickupTimeTo : null
		},

		changePickUpTime({ pickUpTimeFrom, pickUpTimeTo }) {
			this.currentPickUpTimeFrom = pickUpTimeFrom
			this.currentPickUpTimeTo = pickUpTimeTo

			this.isPickUpTimeValid = this.pickUpValidation(
				this.currentPickUpTimeFrom,
				this.currentPickUpTimeTo
			)

			let payload = {
				isNeedShowExpiredPopup: true
			}

			if (this.currentPickUpTimeFrom === 'ASAP') {
				payload = {
					...payload,
					isAsap: true,
					pickupTimeFrom: null,
					pickupTimeTo: null
				}
			} else {
				payload = {
					...payload,
					isAsap: false,
					pickupTimeFrom: this.currentPickUpTimeFrom,
					pickupTimeTo: this.currentPickUpTimeTo
				}
			}

			this.callUpdateCart(payload)
		}
	}
}
</script>

<style lang="scss" scoped>
@import './src/assets/styles/variables';

.order-type-switcher {
	display: flex;
	align-items: center;
	flex-grow: 1;
	margin-right: 8px;

	@media (min-width: 640px) {
		flex-grow: 0;
	}

	.button {
		width: 75px;
		margin: 0 6px;
		min-height: 46px;
		padding: 9px 5px;
		background: transparent;
		border: 1px solid $border-color;
		&:first-child {
			margin-left: 0;
		}
		&:only-child {
			flex-grow: 1;

			@media (min-width: 640px) {
				flex-grow: 0;
			}
		}

		&.read-only {
			pointer-events: none;
		}

		&:hover,
		&.selected {
			background-color: rgba($light-blue, 0.3);
			border-color: transparent;
		}

		@media (min-width: 640px) {
			font-size: 16px;
		}

		@media (min-width: 768px) {
			width: 120px;
		}
	}

	/deep/ .time-picker-holder {
		flex-grow: 1;
		min-width: 150px;

		@media (min-width: 640px) {
			flex-grow: 0;
		}

		&.selected {
			.time-picker-opener--button {
				@media (max-width: 1023px) {
					background-color: rgba($light-blue, 0.3);
					border-color: transparent;
				}
			}
		}

		&.disabled,
		&.read-only {
			pointer-events: none;
		}

		.time-picker {
			width: calc(100vw - 32px);

			@media (min-width: 640px) {
				width: 360px;
				left: 0;
				transform: initial;
			}
		}

		&:not(.pick-up-only) {
			.time-picker {
				width: calc(100vw - 32px);
				left: calc(50% - 13px);
				transform: translateX(-50%);

				@media (min-width: 640px) {
					width: 360px;
					left: 0;
					transform: initial;
				}
			}
		}
	}
}
</style>
